import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import {
  CompliantType,
  MatchKeyMatchableType,
  ModelError_Item,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { forwardRef, Ref, useEffect, useState } from 'react';

import { CompliancePanel } from '~components/CompliancePanel/CompliancePanel';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import MatchKeysSection from '~components/MatchKeys/MatchKeysSection';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { FormStateChangeProps } from '~formsShared/types';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { StagedComplianceDoc } from '~hooks/useComplianceDocuments/checklist';
import { BasicEquipment, Equipment } from '~hooks/useEquipment/models';
import useEquipment from '~hooks/useEquipment/useEquipment';

import { EquipmentForm, EquipmentFormHandlers } from '../EquipmentForm';
import {
  ExternalEquipmentForm,
  ExternalEquipmentFormHandlers,
} from '../ExternalEquipmentForm';

interface EquipmentDialogProps {
  asyncErrors?: ModelError_Item[];
  companyId: string;
  companyShares: CompanyShare[];
  dialogTitle: string;
  isExternalEquipment: boolean;
  isOpen: boolean;
  isUpdating: boolean;
  isUpdatingCompanyShares: boolean;
  managedCompanyId?: string;
  onClose: (reason?: string) => void;
  onFormCompanyChange?: (companyId: string) => void;
  onStagedComplianceDocsChange?: (docs: StagedComplianceDoc[]) => void;
  onSubmit: () => void;
  selectedEquipment: Equipment | BasicEquipment | null;
}

export type EquipmentDialogHandlers =
  | EquipmentFormHandlers
  | ExternalEquipmentFormHandlers;

export const EquipmentDialog = forwardRef<EquipmentDialogHandlers, EquipmentDialogProps>(
  (
    {
      asyncErrors,
      companyId,
      companyShares,
      dialogTitle,
      isExternalEquipment,
      isOpen,
      isUpdating,
      isUpdatingCompanyShares,
      managedCompanyId,
      onClose,
      onFormCompanyChange,
      onStagedComplianceDocsChange,
      onSubmit,
      selectedEquipment,
    },
    ref,
  ) => {
    const { getEquipmentById } = useEquipment();

    const [fullEquipment, setFullEquipment] = useState<Equipment | null>(null);

    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [isComplianceEditing, setIsComplianceEditing] = useState(false);
    const [formCompanyId, setFormCompanyId] = useState(companyId);

    const onFormStateChange = ({ isDirty }: FormStateChangeProps) => {
      setIsDirty(isDirty);
    };

    useEffect(() => {
      if (!isOpen) {
        setFullEquipment(null);
        setIsDirty(false);
        setIsComplianceEditing(false);
        setFormCompanyId(companyId);
      }
    }, [isOpen, companyId]);

    useEffect(() => {
      onFormCompanyChange?.(formCompanyId);
    }, [onFormCompanyChange, formCompanyId]);

    useEffect(() => {
      if (!selectedEquipment) {
        setFullEquipment(null);
        return;
      }

      if (selectedEquipment && !isExternalEquipment) {
        // get the entire Equipment object for internal equipment
        // we need this for the equipment form
        const fetchEquipment = async () => {
          try {
            const equipment = await getEquipmentById(
              selectedEquipment.id,
              companyId,
              managedCompanyId,
            );
            setFullEquipment(equipment);
          } catch {
            // Error is already handled in the hook
            setFullEquipment(null);
          }
        };
        void fetchEquipment();
      }
    }, [selectedEquipment, isExternalEquipment, isOpen]);

    const isFormLoaded =
      !isExternalEquipment && selectedEquipment ? !!fullEquipment : true;

    return (
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        preventCloseOnBackdropClick
        preventCloseOnEscapeKeyDown
      >
        <DialogHeader onClose={onClose}>{dialogTitle}</DialogHeader>

        <DialogContent>
          {!isFormLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : isExternalEquipment ? (
            <ExternalEquipmentForm
              ref={ref as Ref<ExternalEquipmentFormHandlers>}
              companyShares={companyShares}
              defaultEquipment={selectedEquipment}
              errors={asyncErrors}
              managedCompanyId={managedCompanyId}
              onFormStateChange={onFormStateChange}
              selectedCompanyId={companyId}
            />
          ) : (
            <EquipmentForm
              ref={ref as Ref<EquipmentFormHandlers>}
              companyShares={companyShares}
              defaultEquipment={fullEquipment}
              errors={asyncErrors}
              managedCompanyId={managedCompanyId}
              onCompanyChange={setFormCompanyId}
              onFormStateChange={onFormStateChange}
              selectedCompanyId={companyId}
            />
          )}

          {!isExternalEquipment && (
            <Box sx={{ mt: 3 }}>
              {selectedEquipment?.id ? (
                <CompliancePanel
                  compliantType={CompliantType.EQUIPMENT}
                  compliantId={selectedEquipment.id}
                  onComplianceChange={() => setIsDirty(true)}
                  onEditingStateChange={setIsComplianceEditing}
                />
              ) : (
                // Only render (and fetch) while the dialog is open. During the MUI close
                // transition the dialog stays mounted; clearing `selectedEquipment` would
                // otherwise flip us into staged mode and fire a spurious doc-types fetch.
                isOpen && (
                  <CompliancePanel
                    compliantType={CompliantType.EQUIPMENT}
                    companyId={formCompanyId}
                    managedCompanyId={managedCompanyId}
                    stagedMode
                    onStagedDocsChange={onStagedComplianceDocsChange}
                  />
                )
              )}
            </Box>
          )}

          {/* A match key must belong to the company that owns the record, and the endpoint
              authorizes the acting company and its descendants only — never a managed
              company — so managed equipment has no reachable match keys. */}
          {!managedCompanyId && (
            <MatchKeysSection
              companyId={companyId}
              matchableId={selectedEquipment?.id}
              matchableType={MatchKeyMatchableType.EQUIPMENT}
              recordName={selectedEquipment?.name ?? ''}
            />
          )}
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isUpdating || isUpdatingCompanyShares || isComplianceEditing}
            onClick={() => onClose()}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="primary"
            disabled={
              isUpdating || isUpdatingCompanyShares || !isDirty || isComplianceEditing
            }
            loading={isUpdating || isUpdatingCompanyShares}
            onClick={onSubmit}
          >
            {selectedEquipment ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    );
  },
);

EquipmentDialog.displayName = 'EquipmentDialog';
